/**
 * Activity-Score: Strukturelle Tragfähigkeit des Geschäftsmodells.
 *
 * Heuristik (Konzept §4.2 Dimension 2):
 *   - Basis 20
 *   - Wertversprechen formuliert (≥ 30 Zeichen): +10
 *   - Zielkunden definiert (≥ 30 Zeichen): +10
 *   - Klumpenrisiko: NUR wenn Auftraggeberverteilung erfasst ist:
 *     kein Top-Client > 50% Umsatz: +15, sonst 0
 *     (leere Liste → 0, weil fehlende Daten nicht belohnt werden dürfen)
 *   - Wiederkehrende Umsätze ≥ 30%: +20, ≥ 10%: +10, sonst 0
 *   - Skalierbarkeit highly: +15, partly: +8, linear: +0
 *   - Dokumentierter Vertriebsprozess: +10
 *
 * `clampScore` cappt am Ende auf [0,100].
 */
import type { ScoreValue } from '../schema/brands.js';
import type { ActivityData } from '../schema/dimensionData.js';
import { clampScore } from './internal/clamp.js';

export function computeActivityScore(data: ActivityData): ScoreValue {
  let score = 20;

  if ((data.valueProposition ?? '').trim().length >= 30) score += 10;
  if ((data.targetCustomers ?? '').trim().length >= 30) score += 10;

  // Klumpenrisiko nur bewerten, wenn die Verteilung tatsächlich erfasst
  // wurde — leere Liste = unbekannt, nicht „kein Klumpenrisiko".
  if (data.topClientsByRevenue.length > 0) {
    const maxClientShare = data.topClientsByRevenue.reduce(
      (max, c) => Math.max(max, c.revenueSharePercent),
      0,
    );
    if (maxClientShare <= 50) score += 15;
  }

  const recurring = data.recurringRevenueSharePercent ?? 0;
  if (data.hasRecurringRevenue && recurring >= 30) score += 20;
  else if (data.hasRecurringRevenue && recurring >= 10) score += 10;

  if (data.scalability === 'highly_scalable') score += 15;
  else if (data.scalability === 'partly_scalable') score += 8;

  if (data.hasDocumentedSalesProcess) score += 10;

  return clampScore(score);
}
